import { MomentWordConfidence } from '@clipr/lib';
import type { MomentSearchAtom } from './playerTranscriptsState';

export type TranscriptConfidenceLevel =
  'high' |
  'green' |
  'blue' |
  'orange' |
  'red';

export type TranscriptConfidenceSegment = {
  text: string;
  start: number;
  end: number;
  confidence?: number;
  level?: TranscriptConfidenceLevel;
};

export const TranscriptConfidenceLevels: Array<{
  level: TranscriptConfidenceLevel;
  label: string;
  description: string;
}> = [
  {
    level: 'high',
    label: '90%+ Confidence',
    description: 'CLIPr is highly confident this is transcribed correctly.',
  },
  {
    level: 'green',
    label: '80-90% Confidence',
    description: 'CLIPr is confident, but light review may be useful.',
  },
  {
    level: 'blue',
    label: '70-80% Confidence',
    description: 'CLIPr is moderately confident; review suggested.',
  },
  {
    level: 'orange',
    label: '60-70% Confidence',
    description: 'CLIPr is uncertain; review suggested.',
  },
  {
    level: 'red',
    label: '<60% Confidence',
    description: 'CLIPr is not confident; review strongly suggested.',
  },
];

export const getTranscriptConfidenceLevel = (
  confidence: number,
): TranscriptConfidenceLevel => {
  if (confidence >= 0.9)
    return 'high';
  if (confidence >= 0.8)
    return 'green';
  if (confidence >= 0.7)
    return 'blue';
  if (confidence >= 0.6)
    return 'orange';
  return 'red';
};

export const getMomentConfidenceLevel = (
  confidence?: number | null,
): TranscriptConfidenceLevel | null => {
  if (confidence === null || confidence === undefined)
    return null;

  return getTranscriptConfidenceLevel(confidence);
};

export const getSearchMatchForSegment = (
  segment: TranscriptConfidenceSegment,
  searchMatches: MomentSearchAtom[],
): MomentSearchAtom | null => {
  return searchMatches.find(match => {
    const [start, end] = match.indexPair;
    return start < segment.end && end > segment.start;
  }) ?? null;
};

export const buildTranscriptConfidenceSegments = (
  text: string,
  wordConfidence: MomentWordConfidence[],
): TranscriptConfidenceSegment[] => {
  if (wordConfidence.length === 0)
    return [{ text, start: 0, end: text.length }];

  const segments: TranscriptConfidenceSegment[] = [];
  const lowerText = text.toLocaleLowerCase();
  let currentIndex = 0;

  wordConfidence.forEach(({ word, confidence }) => {
    if (!word)
      return;

    const startIndex = lowerText.indexOf(word.toLocaleLowerCase(), currentIndex);
    if (startIndex < 0)
      return;

    if (startIndex > currentIndex) {
      segments.push({
        text: text.slice(currentIndex, startIndex),
        start: currentIndex,
        end: startIndex,
      });
    }

    const endIndex = startIndex + word.length;
    segments.push({
      text: text.slice(startIndex, endIndex),
      start: startIndex,
      end: endIndex,
      confidence,
      level: getTranscriptConfidenceLevel(confidence),
    });

    currentIndex = endIndex;
  });

  if (currentIndex < text.length) {
    segments.push({
      text: text.slice(currentIndex),
      start: currentIndex,
      end: text.length,
    });
  }

  return segments.length > 0 ? segments : [{ text, start: 0, end: text.length }];
};
